/**
 * Landing typography system.
 *
 * `Text` is the only way to render text on the landing. It takes a tag (`as`),
 * a step of the scale (`variant`), and a color (`color`). There is no `sx`,
 * `className`, or `style`, so the scale can only be tuned here.
 *
 * Rules of the scale:
 * - Two weights: regular (400) and medium (500).
 * - Hierarchy comes from color first, then size. Weight is a last resort.
 * - A title and its companion text share a variant and differ by color:
 *   `title` + `title`/secondary, card title `body` + card description `body`/secondary.
 * - Spacing is the parent's job: wrap text in a `Stack` and use `gap`.
 */
import type { HTMLAttributes, ReactNode } from 'react';
import type { SxProps } from './sxProps';
import { useClassName } from './useClassName';
import { styleRule } from './styleRule';

const MOBILE = '@media (max-width: 600px)';

const REGULAR = 400;
const MEDIUM = 500;

const display: SxProps = {
  fontSize: '56px',
  lineHeight: 1.1,
  letterSpacing: '-0.02em',
  fontWeight: REGULAR,
  [MOBILE]: { fontSize: '38px' },
};
const title: SxProps = {
  fontSize: '32px',
  lineHeight: 1.2,
  letterSpacing: '-0.01em',
  fontWeight: REGULAR,
  [MOBILE]: { fontSize: '24px' },
};
const heading: SxProps = {
  fontSize: '20px',
  lineHeight: 1.4,
  [MOBILE]: { fontSize: '18px' },
};
const body: SxProps = { fontSize: '18px', lineHeight: 1.5 };
const small: SxProps = { fontSize: '15px', lineHeight: 1.5 };
const micro: SxProps = { fontSize: '13px', lineHeight: 1.4 };

const VARIANTS = {
  /** Hero headline at the top of a landing page. */
  display,
  /** Page or section title; also its subtitle (with `color="secondary"`) and large figures like prices. */
  title,
  /** Intro paragraph under a hero or page title. */
  lead: { ...heading, fontWeight: REGULAR },
  /** Subsection heading inside a page or an article. */
  heading: { ...heading, fontWeight: MEDIUM },
  /** Running text, card titles, and card descriptions. */
  body: { ...body, fontWeight: REGULAR },
  /** Emphasized running text: names, links, button-like captions. */
  bodyMedium: { ...body, fontWeight: MEDIUM },
  /** Secondary details: meta lines, chips, list items in dense cards. */
  small: { ...small, fontWeight: REGULAR },
  /** Emphasized small text: tags, step captions. */
  smallMedium: { ...small, fontWeight: MEDIUM },
  /** Fine print: copyright, footnotes. */
  micro: { ...micro, fontWeight: REGULAR },
  /** Short uppercase tag: eyebrow above a title, step number, badge, field caption. */
  label: {
    ...micro,
    fontWeight: MEDIUM,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },
} satisfies Record<string, SxProps>;

/**
 * Text colors.
 * `inherit` / `inheritSecondary` are for text on colored or dark surfaces:
 * they take the surface's text color, and the secondary one fades it.
 */
const COLORS = {
  primary: { color: '#121214' },
  secondary: { color: '#66666b' },
  inherit: { color: 'inherit' },
  inheritSecondary: { color: 'inherit', opacity: 0.7 },
  success: { color: '#16a34a' },
  danger: { color: '#d32f2f' },
} satisfies Record<string, SxProps>;

export type TextVariant = keyof typeof VARIANTS;
export type TextColor = keyof typeof COLORS;
export type TextTag = 'h1' | 'h2' | 'h3' | 'h4' | 'p' | 'span' | 'div' | 'li';
export type TextAlign = 'left' | 'center';

export interface TextProps extends Pick<
  HTMLAttributes<HTMLElement>,
  'id' | 'lang' | 'dir' | 'aria-hidden'
> {
  /** HTML tag. Defaults to `p`. */
  as?: TextTag;
  /** Size and weight. Defaults to `body`. */
  variant?: TextVariant;
  /** Defaults to `primary`. */
  color?: TextColor;
  /** Leave unset to inherit alignment from the parent. */
  align?: TextAlign;
  children: ReactNode;
}

export const Text = ({
  as: Tag = 'p',
  variant = 'body',
  color = 'primary',
  align,
  children,
  ...attrs
}: TextProps) => {
  const { className, css, styleId } = useClassName(undefined, {
    margin: 0,
    fontFamily: 'inherit',
    ...VARIANTS[variant],
    ...COLORS[color],
    ...(align ? { textAlign: align } : {}),
  });
  return (
    <>
      {styleRule(styleId, css)}
      <Tag {...attrs} className={className}>
        {children}
      </Tag>
    </>
  );
};
